import {
    DBC_LANGUAGE_SPECIFIC_FIELDS,
    deepCopy,
    PRODUCT_STATE,
    Typography,
} from '../shared/utils';
import { CONTACT_DETAIL } from './dbc-utils';
import { LEAD_COLLECT_TYPE } from '../global-services/dbc.service';

import { BaseModel } from '../global-services/base.model';

export class AgreementLinksModal extends BaseModel {
    label: string;
    url: string;

    __valid_url__: boolean = true;

    constructor(json?) {
        super();

        if (!json) {
            return;
        }

        this.label = json['label'] ? json['label'] : '';
        this.url = json['url'] ? json['url'] : '';
    }
}

export enum DBC_BACKGROUND_TYPE {
    Color = 'color',
    Image = 'image',
}

type contactInfoType =
    | CONTACT_DETAIL.PHONE_V2
    | CONTACT_DETAIL.EMAIL_V2
    | CONTACT_DETAIL.WEBSITE_V2
    | CONTACT_DETAIL.CUSTOM_FIELDS;

interface ContactInfoListType {
    key: contactInfoType;
    value: Array<Object>;
}

export const default_contact_info_ordering: Record<string, contactInfoType> = {
    '0': CONTACT_DETAIL.PHONE_V2,
    '1': CONTACT_DETAIL.EMAIL_V2,
    '2': CONTACT_DETAIL.WEBSITE_V2,
    '3': CONTACT_DETAIL.CUSTOM_FIELDS,
};

export type SocialLinks = {
    facebook: string;
    instagram: string;
    twitter: string;
    linkedin: string;
    tiktok: string;
    snapchat: string;
    whatsapp: string;
    telegram: string;
    vimeo: string;
    youtube: string;
    wistia: string;
    twitch: string;
    discord: string;
    pinterest: string;
    yelp: string;
    paypal: string;
    venmo: string;
    cashapp: string;
    calendly: string;
    shopify: string;
    github: string;
    dribbble: string;
    behance: string;
    custom_url: string;
};

export type Customizations = {
    background_color: string;
    user_info_color: string;
    secondary_color: string;
    font_style: string;
    title_font_size: number;
    font_type: string;
    custom_font_url: string;
    custom_font_style: string;
    profile_info: string;
    company_details: string;
    contact_details: string;
    button: string;
    typography: Typography;
    button_color: string;
    icon_color: string;
    background: {
        type: DBC_BACKGROUND_TYPE;
        value: string;
    };
};

let defaultSocialLinks: SocialLinks = {
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    tiktok: '',
    snapchat: '',
    whatsapp: '',
    telegram: '',
    vimeo: '',
    youtube: '',
    wistia: '',
    twitch: '',
    discord: '',
    pinterest: '',
    yelp: '',
    paypal: '',
    venmo: '',
    cashapp: '',
    calendly: '',
    shopify: '',
    github: '',
    dribbble: '',
    behance: '',
    custom_url: '',
};

export function buildContactInfoList({
    contact_info_ordering = default_contact_info_ordering,
    phone_v2,
    email_v2,
    website_v2,
    custom_fields,
}): Array<ContactInfoListType> {
    const contactInfoList: Array<ContactInfoListType> = [];
    const contactInfoOrdering = contact_info_ordering;

    for (const order in contactInfoOrdering) {
        switch (contactInfoOrdering[order]) {
            case CONTACT_DETAIL.PHONE_V2:
                contactInfoList.push({
                    key: CONTACT_DETAIL.PHONE_V2,
                    value: phone_v2,
                });
                break;
            case CONTACT_DETAIL.EMAIL_V2:
                contactInfoList.push({
                    key: CONTACT_DETAIL.EMAIL_V2,
                    value: email_v2,
                });
                break;
            case CONTACT_DETAIL.WEBSITE_V2:
                contactInfoList.push({
                    key: CONTACT_DETAIL.WEBSITE_V2,
                    value: website_v2,
                });
                break;
            case CONTACT_DETAIL.CUSTOM_FIELDS:
                contactInfoList.push({
                    key: CONTACT_DETAIL.CUSTOM_FIELDS,
                    value: custom_fields,
                });
                break;
        }
    }
    return contactInfoList;
}

export class LeadUserAgreementAttribute {
    user_agreement_urls: Array<AgreementLinksModal> = [
        new AgreementLinksModal({ label: '', url: '' }),
    ];

    constructor(json?) {
        if (!json) {
            return;
        }
        this.user_agreement_urls = json['user_agreement_urls']
            ? json['user_agreement_urls']
            : this.user_agreement_urls;
    }
}

export class DigitalBusinessCard {
    // constants
    INITIAL_DEFAULT_LANGUAGE: string;

    // class members
    id: number;
    state: PRODUCT_STATE;
    url: string;
    first_name: string;
    last_name: string;
    phone: {
        mobile: string;
        work: string;
        home: string;
    } = {
        mobile: '',
        work: '',
        home: '',
    };
    email: string;
    template: any = {};
    website: string;
    address_line1: string;
    address_line2: string;
    city: string;
    // state: '';
    country: string;
    zip: string;
    summary: string;
    logo_url: string;
    logo_size: number;
    designation: string;
    company: string;
    address_url: string;
    social_links: SocialLinks = defaultSocialLinks;
    customizations: Customizations = {
        background_color: '#9b25ff', // Primary Color
        user_info_color: '#FFFFFF', // Profile Text Color
        secondary_color: '#000000', // Secondary Color
        font_style: 'Roboto',
        title_font_size: 30,
        font_type: 'google',
        custom_font_url: '',
        custom_font_style: '',
        profile_info: 'Bold',
        company_details: 'Regular',
        contact_details: 'Medium',
        button: 'Bold',
        typography: {
            font_type: 'google',
            font_family: 'Open Sans',
            personal_info: {
                google_font_style: 'Regular',
                google_font_colour: '#000000',
                google_font_size: 24,
            },
            company_details: {
                google_font_style: 'Regular',
                google_font_colour: '#000000',
                google_font_size: 16,
            },
            contact_details: {
                google_font_style: 'Regular',
                google_font_colour: '#000000',
                google_font_size: 14,
            },
            button: {
                google_font_style: 'Regular',
                google_font_colour: '#FFFFFF',
                google_font_size: 16,
            },
            bio: {
                google_font_style: 'Regular',
                google_font_colour: '#000000',
                google_font_size: 16,
            },
        },
        button_color: '#9b25ff',
        icon_color: '#9b25ff',
        background: {
            type: DBC_BACKGROUND_TYPE.Color,
            value: '#FFFFFF',
        },
    };
    user_image_url: string;
    pronouns_v2: string;
    autodownload_v2: boolean = false;
    address_v2: string;
    phone_v2: Array<Object> = [];
    email_v2: Array<Object> = [];
    website_v2: Array<Object> = [];
    prefix: string;
    suffix: string;
    department: string;
    custom_fields: Array<Object> = [];
    layout: string = '3';
    jwt_update_token = '';
    organization: number;
    maintainer: number;
    threat_active: boolean;
    meta: object;
    slug: string;
    created: string;
    updated: string;
    location_enabled: boolean;
    fb_pixel_id: string;
    google_conversion_id: string;
    name: string;
    card_owner: any;
    address_state: string;
    is_template: boolean = false;
    views: number = 0;
    saves: number = 0;

    lead_collection: boolean;
    lead_attribute: {
        phone: boolean;
        designation: boolean;
        notes: boolean;
        connection: LEAD_COLLECT_TYPE;
        company: boolean;
    } = {
        phone: false,
        designation: false,
        notes: false,
        connection: LEAD_COLLECT_TYPE.SHARE_FIRST,
        company: false,
    };
    card_template: any;
    // multi-language support
    default_language: string = null;
    language_data: { [key: string]: MultiLanguageContent } = {};

    ip_location_enabled: boolean = true;

    // Analytics views and downloads
    __views__: number = 0;
    __downloads__: number = 0;
    // to check if url has to be deleted or not
    __auto_generated_slug__: boolean = true;
    tags: Array<any> = [];
    tags_data: Array<any> = [];
    contact_info_ordering: Record<string, contactInfoType> =
        default_contact_info_ordering;
    __contact_info_list__: Array<ContactInfoListType> = buildContactInfoList({
        ...this,
    });
    __show_lead_form_in_preview__: boolean = false;
    __show_new_sticky_btn__: boolean = false;
    lead_user_agreement: boolean = false;
    lead_user_agreement_attribute: LeadUserAgreementAttribute =
        new LeadUserAgreementAttribute();
    __preview_mobile_url__: string = '';
    __qr_code_url__: string = '';

    constructor(json?) {
        if (!json) {
            return;
        }

        this.id = json['id'] ? json['id'] : this.id;
        this.first_name = json['first_name'] ? json['first_name'] : 'John';
        this.last_name = json['last_name']
            ? json['last_name']
            : this.first_name
              ? ''
              : 'Doe';
        this.phone = json['phone'] ? json['phone'] : this.phone;
        this.email = json['email'] ? json['email'] : this.email;
        this.website = json['website'] ? json['website'] : this.website;
        this.address_line1 = json['address_line1']
            ? json['address_line1']
            : this.address_line1;
        this.address_line2 = json['address_line2']
            ? json['address_line2']
            : this.address_line2;
        this.city = json['city'] ? json['city'] : this.city;
        this.country = json['country'] ? json['country'] : this.country;
        this.zip = json['zip'] ? json['zip'] : this.zip;
        this.summary = json['summary'] ? json['summary'] : '';
        this.logo_url = json['logo_url'] ? json['logo_url'] : this.logo_url;
        this.logo_size = this.initializeLogoSizeFromTemplate(
            json['layout'],
            json['logo_size']
        );
        this.designation = json['designation']
            ? json['designation']
            : this.designation;
        this.company = json['company'] ? json['company'] : this.company;
        this.address_url = json['address_url']
            ? json['address_url']
            : this.address_url;
        this.customizations = json['customizations']
            ? json['customizations']
            : this.customizations;
        this.city = json['city'] ? json['city'] : this.city;
        this.user_image_url = json['user_image_url']
            ? json['user_image_url']
            : this.user_image_url;
        this.social_links = json['social_links']
            ? json['social_links']
            : this.social_links;
        this.pronouns_v2 = json['pronouns_v2']
            ? json['pronouns_v2']
            : this.pronouns_v2;
        this.autodownload_v2 = json.hasOwnProperty('autodownload_v2')
            ? json['autodownload_v2']
            : this.autodownload_v2;
        this.address_v2 = json['address_v2']
            ? json['address_v2']
            : this.address_v2;
        this.phone_v2 = json['phone_v2'] ? json['phone_v2'] : this.phone_v2;
        this.email_v2 = json['email_v2'] ? json['email_v2'] : this.email_v2;
        this.website_v2 = json['website_v2']
            ? json['website_v2']
            : this.website_v2;
        this.custom_fields = json['custom_fields']
            ? json['custom_fields']
            : this.custom_fields;
        this.prefix = json['prefix'] ? json['prefix'] : this.prefix;
        this.suffix = json['suffix'] ? json['suffix'] : this.suffix;
        this.department = json['department']
            ? json['department']
            : this.department;
        this.layout = json['layout'] ? json['layout'] : this.layout;
        this.jwt_update_token = json['jwt_update_token']
            ? json['jwt_update_token']
            : '';
        this.organization = json['organization'] || null;
        this.maintainer = json['maintainer'];
        this.threat_active = json['threat_active'] || false;
        this.meta = json['meta'] || {};
        this.slug = json['slug'];
        this.created = json['created'];
        this.updated = json['updated'];
        this.location_enabled = json.hasOwnProperty('location_enabled')
            ? json['location_enabled']
            : true;
        this.fb_pixel_id = json['fb_pixel_id'];
        this.google_conversion_id = json['google_conversion_id'];
        this.state = json['state'] || PRODUCT_STATE.ACTIVE;
        this.url = json['url'];
        this.name =
            json['first_name'] +
            (this.last_name.length > 0 ? ' ' + json['last_name'] : '');
        this.card_owner = json['card_owner'] ? json['card_owner'] : {};
        this.address_state = json['address_state'] ? json['address_state'] : '';
        this.is_template = json['is_template'] || false;
        this.lead_collection = json.hasOwnProperty('lead_collection')
            ? json['lead_collection']
            : true;
        this.lead_attribute = json['lead_attribute']
            ? json['lead_attribute']
            : this.lead_attribute;
        this.template = json['template'] || {};
        this.default_language = json['default_language']
            ? json['default_language']
            : this.default_language;
        this.populateLanguageData(
            json['language_data'] ? json['language_data'] : {},
            json ? json : {}
        );
        this.__views__ = json['__views__'] || 0;
        this.__downloads__ = json['__downloads__'] || 0;
        this.INITIAL_DEFAULT_LANGUAGE = 'en';
        this.card_template = json['card_template']
            ? json['card_template']
            : this.card_template;
        this.ip_location_enabled = json.hasOwnProperty('ip_location_enabled')
            ? json['ip_location_enabled']
            : true;
        this.views = json['views'] || this.views;
        this.saves = json['saves'] || this.saves;
        this.tags = json['tags'] || this.tags;
        this.tags_data = json['tags_data'] || this.tags_data;
        this.contact_info_ordering =
            json['contact_info_ordering'] || this.contact_info_ordering;
        this.__contact_info_list__ =
            buildContactInfoList({ ...json }) || this.__contact_info_list__;
        this.lead_user_agreement = json.hasOwnProperty('lead_user_agreement')
            ? json['lead_user_agreement']
            : false;
        this.setMobilePreviewUrl();
        this.set_lead_user_agreement_agreement(json);
        this.__qr_code_url__ = json['__qr_code_url__'];
    }

    get full_name() {
        return this.first_name + ' ' + this.last_name;
    }

    setMobilePreviewUrl() {
        const screenHeight = window.innerHeight;

        let zoom = 0.7; // Default zoom

        if (screenHeight <= 667) {
            // Check for iPhone SE (375x667 screen size)
            zoom = 0.6; // Adjust zoom for iPhone SE
        }

        this.__preview_mobile_url__ = `${this.url}?preview=true&zoom=${zoom}&pwa_preview=true`;
    }

    set_lead_user_agreement_agreement(json) {
        if (!json) {
            return;
        }

        this.lead_user_agreement_attribute =
            json['lead_user_agreement_attribute'] ||
            this.lead_user_agreement_attribute;
        if (!this.lead_user_agreement_attribute?.user_agreement_urls?.length) {
            this.lead_user_agreement_attribute.user_agreement_urls = [
                new AgreementLinksModal({ label: '', url: '' }),
            ];
        }
    }

    initializeLogoSizeFromTemplate(
        dbcLayout: string,
        logoSize: number
    ): number {
        if (logoSize) {
            return logoSize;
        } else {
            switch (dbcLayout) {
                case '1':
                    return 80;
                case '2':
                    return 30;
                case '3':
                    return 25;
                case '4':
                    return 75;
                case '5':
                    return 30;
                case '6':
                    return 35;
                default:
                    return 30;
            }
        }
    }

    populateLanguageData(language_data: any, json: DigitalBusinessCard) {
        this.language_data = {};
        if (this.default_language) {
            for (const code in language_data) {
                this.language_data[code] = new MultiLanguageContent(
                    language_data[code]
                );
            }
            this.language_data[this.default_language] =
                new MultiLanguageContent();
            for (const field of DBC_LANGUAGE_SPECIFIC_FIELDS) {
                this.language_data[this.default_language][field] = deepCopy(
                    this[field]
                );
            }
            this.language_data[this.default_language]['__contact_info_list__'] =
                buildContactInfoList({ ...json });
        }
    }

    clearModelForPost() {
        delete this.id;
        if (this.__auto_generated_slug__) {
            delete this.url;
        }
        delete this.__views__;
        delete this.__downloads__;
        delete this.__auto_generated_slug__;
        delete this.__contact_info_list__;
    }

    clearModelForPut() {
        delete this.url;
        delete this.__views__;
        delete this.__downloads__;
        delete this.__auto_generated_slug__;
        delete this.INITIAL_DEFAULT_LANGUAGE;
        delete this.__contact_info_list__;
    }

    // if fetchDataFromLanguageDataModel is called with a parameter(lang) we populate 18 fields of DBC Model with data stored in language_data[lang]
    // otherwise we populate 18 fields of DBC Model with data stored in language_data[this.default_language]
    fetchDataFromLanguageDataModel(lang?: string) {
        const language = lang ? lang : this.default_language;
        if (language && this.language_data[language]) {
            const languageData = this.language_data[language];
            const fieldsToPopulate = [
                'first_name',
                'last_name',
                'prefix',
                'suffix',
                'designation',
                'department',
                'pronouns_v2',
                'company',
                'summary',
                'logo_url',
                'phone',
                'phone_v2',
                'email',
                'email_v2',
                'website',
                'website_v2',
                'custom_fields',
                'address_url',
                'address_v2',
                'social_links',
                'user_image_url',
                '__contact_info_list__',
            ];
            fieldsToPopulate.forEach((fieldName) => {
                this[fieldName] = languageData[fieldName];
            });
        }
    }
}

export class DigitalBusinessCardSetting {
    first_name: boolean = undefined;
    last_name: boolean = false;
    phone: boolean = false;
    email: boolean = false;
    website: boolean = false;
    address: boolean = false;
    summary: boolean = false;
    logo_url: boolean = false;
    designation: boolean = false;
    company: boolean = false;
    social_links: boolean = false;
    customizations: boolean = false;
    user_image_url: boolean = false;
    pronouns: boolean = false;
    autodownload: boolean = false;
    prefix: boolean = false;
    suffix: boolean = false;
    department: boolean = false;
    custom_fields: boolean = false;
    layout: boolean = false;
    location_enabled: boolean = false;
    lead_collection: boolean = false;
    custom_labels: Array<String> = [];
    organization: number = null;
    template: boolean = false;

    // New fields
    id: number = null;
    enforce_card_template: boolean = false;
    email_wallet_pass: boolean = false;
    ip_location_enabled: boolean = false;
    lead_user_agreement: boolean = false;

    constructor(json?) {
        if (!json) {
            return;
        }
        this.first_name = json['first_name'] || false;
        this.last_name = json['last_name'] || false;
        this.phone = json['phone'] || false;
        this.email = json['email'] || false;
        this.website = json['website'] || false;
        this.address = json['address'] || false;
        this.summary = json['summary'] || false;
        this.logo_url = json['logo_url'] || false;
        this.designation = json['designation'] || false;
        this.company = json['company'] || false;
        this.customizations = json['customizations'] || false;
        this.user_image_url = json['user_image_url'] || false;
        this.social_links = json['social_links'] || false;
        this.pronouns = json['pronouns'] || false;
        this.custom_fields = json['custom_fields'] || false;
        this.prefix = json['prefix'] || false;
        this.suffix = json['suffix'] || false;
        this.department = json['department'] || false;
        this.layout = json['layout'] || false;
        this.autodownload = json['autodownload'] || false;
        this.location_enabled = json['location_enabled'] || false;
        this.lead_collection = json['lead_collection'] || false;
        this.custom_labels = json['custom_labels'] || [];
        this.organization = json['organization'] || null;
        this.template = json['template'] || false;

        this.id = json['id'];
        this.enforce_card_template = json['enforce_card_template'] || false;
        this.email_wallet_pass = json['email_wallet_pass'] || false;
        this.ip_location_enabled = json['ip_location_enabled'] || false;
        this.lead_user_agreement = json.hasOwnProperty('lead_user_agreement')
            ? json['lead_user_agreement']
            : false;
    }
}

export class MultiLanguageContent {
    first_name: string = '';
    last_name: string = '';
    prefix: string = '';
    suffix: string = '';
    designation: string = '';
    department: string = '';
    pronouns_v2: string = '';
    company: string = '';
    summary: string = '';
    logo_url: string = '';
    phone_v2: Array<Object> = [];
    email_v2: Array<Object> = [];
    website_v2: Array<Object> = [];
    custom_fields: Array<Object> = [];
    address_url: string;
    address_v2: string;
    social_links: SocialLinks = defaultSocialLinks;
    user_image_url: string = '';
    contact_info_ordering: Record<string, contactInfoType> =
        default_contact_info_ordering;
    __contact_info_list__: Array<any> = buildContactInfoList({ ...this });

    constructor(json?) {
        if (!json) {
            return;
        }
        this.first_name = json['first_name']
            ? json['first_name']
            : this.first_name;
        this.last_name = json['last_name'] ? json['last_name'] : this.last_name;
        this.summary = json['summary'] ? json['summary'] : this.summary;
        this.logo_url = json['logo_url'] ? json['logo_url'] : this.logo_url;
        this.designation = json['designation']
            ? json['designation']
            : this.designation;
        this.company = json['company'] ? json['company'] : this.company;
        this.address_url = json['address_url']
            ? json['address_url']
            : this.address_url;
        this.user_image_url = json['user_image_url']
            ? json['user_image_url']
            : this.user_image_url;
        this.social_links = json['social_links']
            ? json['social_links']
            : this.social_links;
        this.pronouns_v2 = json['pronouns_v2']
            ? json['pronouns_v2']
            : this.pronouns_v2;
        this.address_v2 = json['address_v2']
            ? json['address_v2']
            : this.address_v2;
        this.phone_v2 = json['phone_v2'] ? json['phone_v2'] : this.phone_v2;
        this.email_v2 = json['email_v2'] ? json['email_v2'] : this.email_v2;
        this.website_v2 = json['website_v2']
            ? json['website_v2']
            : this.website_v2;
        this.custom_fields = json['custom_fields']
            ? json['custom_fields']
            : this.custom_fields;
        this.prefix = json['prefix'] ? json['prefix'] : this.prefix;
        this.suffix = json['suffix'] ? json['suffix'] : this.suffix;
        this.department = json['department']
            ? json['department']
            : this.department;
        this.contact_info_ordering =
            json['contact_info_ordering'] || this.contact_info_ordering;
        this.__contact_info_list__ =
            buildContactInfoList({ ...json }) || this.__contact_info_list__;
    }
}
